import React, { useEffect, useRef, useState } from "react";
import {
  ChevronRight,
  Send,
  ShieldCheck,
  Upload,
  FileCheck,
  AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../api/api";
import toast from "react-hot-toast";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const complainantCategories = [
  "Licensed free zone enterprise",
  "Free zone operator or management company",
  "Investor or prospective investor",
  "Contractor or service provider",
  "Employee or former employee",
  "Host community member",
  "Government agency",
  "Logistics or port user",
  "Member of the public",
  "OGFZA staff",
  "Other",
];

const complaintTypes = [
  "Licence and permit complaint",
  "Free zone operator complaint",
  "Tariff, levy or charge complaint",
  "Delay in regulatory approval",
  "Investor service complaint",
  "Customs, port or logistics-related issue",
  "Employment or labour-related dispute",
  "Environmental, health and safety complaint",
  "Security concern within free zone",
  "Corruption, extortion or unethical conduct",
  "Service delivery complaint against OGFZA staff",
  "Host community-related complaint",
  "General enquiry or feedback",
  "Other",
];

const freeZones = [
  "Onne Oil and Gas Free Zone",
  "Warri Oil and Gas Free Zone",
  "Liberty Oil and Gas Free Zone",
  "Brass Oil and Gas Free Zone",
  "Notore Industrial City",
  "Eko Support Free Zone",
  "BESTAF Free Zone",
  "SPFL Free Zone",
  "Orashi Special Energy Free Zone",
  "Secured Bunkering Anchorage",
  "Other OGFZA-regulated zone",
  "Not applicable",
  "Other",
];

const initialForm = {
  full_name: "",
  organisation: "",
  designation: "",
  phone: "",
  email: "",
  contact_address: "",
  preferred_contact_method: "",
  submitting_on_behalf: false,
  is_licensed_operator: false,
  licence_number: "",
  anonymous: false,

  complainant_category: "",
  complaint_type: "",

  free_zone: "",
  company_involved: "",
  location_within_zone: "",
  facility_or_unit: "",
  incident_date: "",
  incident_time: "",

  subject: "",
  description: "",
  persons_involved: "",
  action_taken: "",
  reported_before: false,
  previous_reference: "",
  requested_outcome: "",

  urgent: false,
  financial_loss: false,
  amount_involved: "",
  affects_business_operations: false,

  evidence_files: [],

  confirm_truth: false,
  consent: false,
  allow_contact: true,

  honeypot: "",
};

const ComplaintPage = () => {
  const pageRef = useRef(null);
  const [form, setForm] = useState(initialForm);
  const [uploading, setUploading] = useState(false);
  const [tracking, setTracking] = useState("");

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", { opacity: 0, y: 30, duration: 0.8 });
      gsap.from(".hero-title", { opacity: 0, y: 80, duration: 1.1 });
      gsap.from(".hero-text", { opacity: 0, y: 40, duration: 0.9, delay: 0.25 });
      gsap.from(".complaint-form-card", { opacity: 0, y: 60, duration: 1, delay: 0.3 });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const uploadEvidence = async (files) => {
    const selectedFiles = Array.from(files || []);

    if (!selectedFiles.length) return;

    if (form.evidence_files.length + selectedFiles.length > 10) {
      toast.error("You can upload a maximum of 10 evidence files.");
      return;
    }

    try {
      setUploading(true);

      const uploadedUrls = [];

      for (const file of selectedFiles) {
        const body = new FormData();
        body.append("file", file);

        const res = await api.post("/complaints/evidence-upload", body, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        uploadedUrls.push(res.data.url);
      }

      setForm((prev) => ({
        ...prev,
        evidence_files: [...prev.evidence_files, ...uploadedUrls],
      }));

      toast.success("Evidence uploaded successfully");
    } catch (err) {
      toast.error(err.cleanMessage || "Evidence upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...form,
        full_name: form.anonymous ? "" : form.full_name,
      };

      const res = await api.post("/complaints", payload);

      setTracking(res.data.tracking_number);
      toast.success("Complaint submitted successfully");
      setForm(initialForm);
    } catch (err) {
      toast.error(err.cleanMessage || "Complaint submission failed");
    }
  };

  return (
    <main ref={pageRef} className="bg-slate-50 overflow-hidden">
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="OGFZA Complaint Form"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />

          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />

          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Regulatory Feedback
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Lay your Complaint 
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Submit complaints, reports or feedback relating to operations, licensing, approvals, tariffs, conduct, investment facilitation and other regulatory matters within OGFZA-regulated free zones.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">
                Home
              </Link>
              <ChevronRight size={14} />
              <span className="text-[#ff6600]">Complaints</span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        {tracking && (
          <div className="mb-8 bg-green-50 border border-green-200 rounded-3xl p-6">
            <h3 className="font-black text-green-700 text-xl">
              Complaint Submitted Successfully
            </h3>
            <p className="text-green-700 mt-2">
              Please keep your complaint reference number for tracking:
            </p>
            <p className="text-2xl md:text-3xl font-black text-[#06183f] mt-2">
              {tracking}
            </p>
            <Link
              to="/contact-help/track-complaint"
              className="inline-flex mt-4 px-5 py-3 rounded-2xl bg-[#06183f] text-white font-bold"
            >
              Track Complaint
            </Link>
          </div>
        )}

        <form
          onSubmit={submit}
          className="complaint-form-card bg-white rounded-[34px] p-6 md:p-8 shadow-[0_18px_45px_rgba(0,0,0,0.12)] border border-slate-100"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center shrink-0">
              <ShieldCheck size={26} />
            </div>

            <div>
              <h2
                className="text-2xl md:text-3xl font-black text-[#06183f]"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                OGFZA Regulatory Complaint Portal
              </h2>
              <p className="text-slate-500 text-sm">
                All complaints are treated confidentially and assigned to the appropriate department.
              </p>
            </div>
          </div>

          <input
            type="text"
            value={form.honeypot}
            onChange={(e) => update("honeypot", e.target.value)}
            className="hidden"
            tabIndex="-1"
            autoComplete="off"
          />

          <div className="mb-8 p-5 rounded-3xl bg-[#06183f]/5 border border-[#06183f]/10">
            <label className="flex items-start gap-3 font-bold text-[#06183f]">
              <input
                type="checkbox"
                checked={form.anonymous}
                onChange={(e) => update("anonymous", e.target.checked)}
                className="mt-1"
              />
              Submit this complaint anonymously
            </label>
            <p className="text-slate-600 text-sm mt-2 leading-relaxed">
              Anonymous reporting is allowed for sensitive complaints such as corruption, extortion, abuse of office or unethical conduct. However, anonymous complaints may be harder to investigate where evidence is limited.
            </p>
          </div>

          <h3 className="text-xl font-black text-[#06183f] mb-4">1. Complainant Information</h3>

          <div className="grid md:grid-cols-2 gap-4">
            {!form.anonymous && (
              <>
                <input required placeholder="Full Name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
                <input placeholder="Organisation / Company Name" value={form.organisation} onChange={(e) => update("organisation", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
                <input placeholder="Designation" value={form.designation} onChange={(e) => update("designation", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
              </>
            )}

            <input required={!form.anonymous} placeholder="Phone Number" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
            <input required={!form.anonymous} type="email" placeholder="Email Address" value={form.email} onChange={(e) => update("email", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
            <input placeholder="Contact Address" value={form.contact_address} onChange={(e) => update("contact_address", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />

            <select value={form.preferred_contact_method} onChange={(e) => update("preferred_contact_method", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] bg-white">
              <option value="">Preferred Contact Method</option>
              <option>Email</option>
              <option>Phone</option>
              <option>SMS</option>
              <option>Official Letter</option>
            </select>

            <input placeholder="OGFZA Licence Number, if applicable" value={form.licence_number} onChange={(e) => update("licence_number", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <label className="flex items-center gap-3 bg-slate-50 rounded-2xl p-4 font-semibold text-slate-700">
              <input type="checkbox" checked={form.submitting_on_behalf} onChange={(e) => update("submitting_on_behalf", e.target.checked)} />
              Submitting on behalf of an organisation
            </label>

            <label className="flex items-center gap-3 bg-slate-50 rounded-2xl p-4 font-semibold text-slate-700">
              <input type="checkbox" checked={form.is_licensed_operator} onChange={(e) => update("is_licensed_operator", e.target.checked)} />
              Licensed free zone operator / enterprise
            </label>
          </div>

          <h3 className="text-xl font-black text-[#06183f] mt-10 mb-4">2. Complaint Classification</h3>

          <div className="grid md:grid-cols-2 gap-4">
            <select required value={form.complainant_category} onChange={(e) => update("complainant_category", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] bg-white">
              <option value="">Select Complainant Category</option>
              {complainantCategories.map((item) => <option key={item}>{item}</option>)}
            </select>

            <select required value={form.complaint_type} onChange={(e) => update("complaint_type", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] bg-white">
              <option value="">Select Complaint Type</option>
              {complaintTypes.map((item) => <option key={item}>{item}</option>)}
            </select>
          </div>

          <h3 className="text-xl font-black text-[#06183f] mt-10 mb-4">3. Free Zone or Organisation Involved</h3>

          <div className="grid md:grid-cols-2 gap-4">
            <select value={form.free_zone} onChange={(e) => update("free_zone", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] bg-white">
              <option value="">Select Free Zone</option>
              {freeZones.map((item) => <option key={item}>{item}</option>)}
            </select>

            <input placeholder="Company or Operator Involved" value={form.company_involved} onChange={(e) => update("company_involved", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
            <input placeholder="Location within the Zone" value={form.location_within_zone} onChange={(e) => update("location_within_zone", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
            <input placeholder="Terminal, Facility, Office or Unit Involved" value={form.facility_or_unit} onChange={(e) => update("facility_or_unit", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
            <input type="date" value={form.incident_date} onChange={(e) => update("incident_date", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
            <input type="time" value={form.incident_time} onChange={(e) => update("incident_time", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
          </div>

          <h3 className="text-xl font-black text-[#06183f] mt-10 mb-4">4. Complaint Details</h3>

          <input required placeholder="Subject of Complaint" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />

          <textarea required rows="7" placeholder="Detailed Description of Complaint" value={form.description} onChange={(e) => update("description", e.target.value)} className="mt-4 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] resize-none" />

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <textarea rows="4" placeholder="Names of persons, companies or departments involved" value={form.persons_involved} onChange={(e) => update("persons_involved", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] resize-none" />

            <textarea rows="4" placeholder="What action have you already taken?" value={form.action_taken} onChange={(e) => update("action_taken", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] resize-none" />

            <input placeholder="Previous Complaint Reference Number, if any" value={form.previous_reference} onChange={(e) => update("previous_reference", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />

            <input placeholder="Estimated Amount Involved, if any" value={form.amount_involved} onChange={(e) => update("amount_involved", e.target.value)} className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f]" />
          </div>

          <textarea rows="4" placeholder="What outcome are you requesting?" value={form.requested_outcome} onChange={(e) => update("requested_outcome", e.target.value)} className="mt-4 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#06183f] resize-none" />

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {[
              ["reported_before", "Have you reported this issue before?"],
              ["urgent", "Is this issue urgent?"],
              ["financial_loss", "Does this issue involve financial loss?"],
              ["affects_business_operations", "Does this issue affect business operations?"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-3 bg-slate-50 rounded-2xl p-4 font-semibold text-slate-700">
                <input type="checkbox" checked={form[key]} onChange={(e) => update(key, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>

          <h3 className="text-xl font-black text-[#06183f] mt-10 mb-4">5. Evidence Upload</h3>

          <div className="border border-dashed border-slate-300 rounded-2xl p-5">
            <label className="flex items-center gap-3 font-bold text-[#06183f] cursor-pointer">
              <Upload size={20} />
              Upload Evidence
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.xls,.xlsx"
                onChange={(e) => uploadEvidence(e.target.files)}
                className="hidden"
              />
            </label>

            <p className="text-slate-500 text-sm mt-2">
              Accepted: PDF, DOC, DOCX, JPG, PNG, XLS, XLSX. Maximum 10MB per file.
            </p>

            {uploading && <p className="text-sm text-slate-500 mt-2">Uploading...</p>}

            {form.evidence_files.length > 0 && (
              <div className="mt-4 space-y-2">
                {form.evidence_files.map((file, index) => (
                  <p key={file} className="flex items-center gap-2 text-sm text-green-700">
                    <FileCheck size={16} />
                    Evidence file {index + 1} uploaded
                  </p>
                ))}
              </div>
            )}
          </div>

          <h3 className="text-xl font-black text-[#06183f] mt-10 mb-4">6. Declaration and Consent</h3>

          <div className="space-y-3">
            <label className="flex items-start gap-3 text-slate-700 font-semibold">
              <input type="checkbox" checked={form.confirm_truth} onChange={(e) => update("confirm_truth", e.target.checked)} className="mt-1" required />
              I confirm that the information provided is true to the best of my knowledge.
            </label>

            <label className="flex items-start gap-3 text-slate-700 font-semibold">
              <input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} className="mt-1" required />
              I consent to OGFZA using the information provided to review, investigate and resolve this complaint.
            </label>

            <label className="flex items-start gap-3 text-slate-700 font-semibold">
              <input type="checkbox" checked={form.allow_contact} onChange={(e) => update("allow_contact", e.target.checked)} className="mt-1" />
              I agree to be contacted for further clarification where necessary.
            </label>
          </div>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-3xl p-5 flex gap-3">
            <AlertCircle className="text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800 leading-relaxed">
              OGFZA will treat your complaint with confidentiality and use the information provided only for review, investigation, regulatory action, reporting and resolution purposes.
            </p>
          </div>

          <button className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-[#06183f] hover:bg-[#ff6600] text-white font-black transition-colors">
            Submit Complaint <Send size={18} />
          </button>
        </form>
      </section>
    </main>
  );
};

export default ComplaintPage;